import { useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import {
  resolveShiftOptions,
  resolveIsMultiShift,
  type ShiftOption,
} from 'utils/helpers/customerShiftHelpers';

interface UseShiftConfig {
  shiftPeriods: ShiftOption[];
  shiftOptions: ShiftOption[];
  isMultiShift: boolean;
}

const EMPTY_SHIFT_PERIODS: ShiftOption[] = [];

/**
 * Global source of truth for a customer's shift configuration.
 *
 * Reads the backend-driven shift config from Redux
 * (`customerShiftConfigReducer`) and falls back to the hardcoded
 * `ShiftDetails` map only when the API config isn't available.
 */
const useShiftConfig = (): UseShiftConfig => {
  const rawShiftPeriods = useAppSelector(
    (state) => state.customerShiftConfigReducer.data?.shiftPeriods
  );
  const shiftPeriods = rawShiftPeriods ?? EMPTY_SHIFT_PERIODS;

  const customerCode = useAppSelector(
    (state) => state.authReducer.customer?.code ?? ''
  );

  const shiftOptions = useMemo(
    () => resolveShiftOptions(shiftPeriods, customerCode),
    [shiftPeriods, customerCode]
  );

  const isMultiShift = useMemo(
    () => resolveIsMultiShift(shiftPeriods, customerCode),
    [shiftPeriods, customerCode]
  );

  return useMemo(
    () => ({
      shiftPeriods,
      shiftOptions,
      isMultiShift,
    }),
    [shiftPeriods, shiftOptions, isMultiShift]
  );
};

export default useShiftConfig;
